// ── EmployeeList — 직원 CRUD ────────────────────────────────
import React, { useState } from 'react';

const EMP_TYPES = [
  { id: 'fulltime',   label: '정직원',     desc: '월급제·4대보험 가입' },
  { id: 'parttime',   label: '시급제 알바', desc: '시급 기준, 4대보험 선택' },
  { id: 'shortterm',  label: '단기 (월 60h 미만)', desc: '시급 기준, 60h 미만 시 4대보험 자동 면제 + 3.3%' },
  { id: 'daily',      label: '일용직',     desc: '일급 기준, 4대보험 X, 일 18.7만원 비과세 + 초과분 6.6%' },
  { id: 'freelancer', label: '사업소득자',  desc: '3.3% 원천징수' },
];

const emptyEmp = () => ({
  id: '', name: '', type: 'parttime',
  hourlyWage: 0, monthlyWage: 0, dailyRate: 0,
  fourInsurance: { enabled: false, dependents: 1 },
  hireDate: new Date().toISOString().slice(0, 10),
  position: '', active: true,
});

const fmt = n => Math.round(Number(n) || 0).toLocaleString('ko-KR');

export default function EmployeeList({ employees = [], onChange }) {
  const [editing, setEditing] = useState(null); // null | new emp object | existing
  const [confirmDelete, setConfirmDelete] = useState(null);

  const startNew = () => setEditing({ ...emptyEmp(), id: 'e' + Date.now() });
  const startEdit = e => setEditing({ ...e });
  const cancel = () => setEditing(null);

  const save = () => {
    if (!editing.name?.trim()) { alert('이름을 입력하세요'); return; }
    if (editing.type === 'daily') {
      if (!editing.dailyRate) { alert('일용직은 일급을 입력하세요'); return; }
    } else if (!editing.hourlyWage && !editing.monthlyWage) {
      alert('시급 또는 월급을 입력하세요');
      return;
    }
    const exists = employees.some(e => e.id === editing.id);
    const next = exists
      ? employees.map(e => e.id === editing.id ? editing : e)
      : [...employees, editing];
    onChange(next);
    setEditing(null);
  };

  const remove = id => {
    onChange(employees.filter(e => e.id !== id));
    setConfirmDelete(null);
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    flexWrap: 'wrap', gap: '8px', marginBottom: '14px' }}>
        <h3 style={{ fontSize: '16px', fontWeight: '700', color: '#18181B', margin: 0 }}>👥 직원 관리</h3>
        <button onClick={startNew}
          style={{ padding: '8px 16px', borderRadius: '8px', border: 'none',
                   background: '#FF6F0F', color: '#fff', fontSize: '13px',
                   fontWeight: '700', cursor: 'pointer', flexShrink: 0 }}>
          + 직원 추가
        </button>
      </div>

      {/* 직원 목록 */}
      <div style={{ background: '#fff', borderRadius: '10px', overflow: 'hidden',
                    border: '1px solid #e5e7eb' }}>
        {employees.length === 0 ? (
          <div style={{ padding: '32px', textAlign: 'center', color: '#9ca3af', fontSize: '13px' }}>
            등록된 직원이 없습니다. <strong>+ 직원 추가</strong> 버튼을 눌러 시작하세요.
          </div>
        ) : (
          employees.map((e, i) => {
            const typeLabel = EMP_TYPES.find(t => t.id === e.type)?.label || e.type;
            return (
              <div key={e.id} style={{
                padding: '14px 16px',
                borderBottom: i < employees.length - 1 ? '1px solid #f3f4f6' : 'none',
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                flexWrap: 'wrap', gap: '10px',
              }}>
                <div style={{ flex: '1 1 200px', minWidth: 0 }}>
                  <div style={{ fontSize: '14px', fontWeight: '700', color: '#111' }}>
                    {e.name}
                    {!e.active && <span style={{ marginLeft: 8, fontSize: 11, color: '#9ca3af' }}>(휴직)</span>}
                  </div>
                  <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '3px' }}>
                    {typeLabel}
                    {e.position && ` · ${e.position}`}
                    {e.hourlyWage > 0 && ` · 시급 ${fmt(e.hourlyWage)}원`}
                    {e.monthlyWage > 0 && ` · 월급 ${fmt(e.monthlyWage)}원`}
                    {e.dailyRate > 0 && ` · 일급 ${fmt(e.dailyRate)}원`}
                    {e.fourInsurance?.enabled && ' · 4대보험 가입'}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
                  <button onClick={() => startEdit(e)}
                    style={{ padding: '5px 10px', borderRadius: '6px', border: '1px solid #e5e7eb',
                             background: '#fff', color: '#374151', fontSize: '12px', cursor: 'pointer' }}>
                    편집
                  </button>
                  <button onClick={() => setConfirmDelete(e.id)}
                    style={{ padding: '5px 10px', borderRadius: '6px', border: '1px solid #E4E4E7',
                             background: '#fff5f5', color: '#52525B', fontSize: '12px', cursor: 'pointer' }}>
                    삭제
                  </button>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* 편집 모달 */}
      {editing && (
        <EmployeeForm
          value={editing}
          onChange={setEditing}
          onSave={save}
          onCancel={cancel}
          isNew={!employees.some(e => e.id === editing.id)}
        />
      )}

      {/* 삭제 확인 */}
      {confirmDelete && (
        <div onClick={() => setConfirmDelete(null)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000,
                   display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}>
          <div onClick={e => e.stopPropagation()}
            style={{ background: '#fff', borderRadius: '12px', padding: '20px', maxWidth: '320px', width: '100%' }}>
            <p style={{ fontSize: '14px', color: '#374151', margin: '0 0 16px' }}>
              이 직원을 삭제하시겠습니까? <br />
              <strong>스케줄·급여 기록도 같이 사라집니다.</strong>
            </p>
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <button onClick={() => setConfirmDelete(null)}
                style={{ padding: '8px 16px', borderRadius: '6px', border: '1px solid #e5e7eb',
                         background: '#fff', cursor: 'pointer', fontSize: '13px' }}>취소</button>
              <button onClick={() => remove(confirmDelete)}
                style={{ padding: '8px 16px', borderRadius: '6px', border: 'none',
                         background: '#52525B', color: '#fff', cursor: 'pointer', fontSize: '13px', fontWeight: '700' }}>
                삭제
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── 직원 등록/편집 폼 모달 ──
function EmployeeForm({ value, onChange, onSave, onCancel, isNew }) {
  const v = value;
  const set = (k, val) => onChange({ ...v, [k]: val });
  const setIns = (k, val) => onChange({ ...v, fourInsurance: { ...v.fourInsurance, [k]: val } });

  const inp = {
    width: '100%', padding: '8px 10px', border: '1px solid #d1d5db',
    borderRadius: '6px', fontSize: '13px', boxSizing: 'border-box',
  };

  return (
    <div onClick={onCancel}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000,
               display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px',
               overflow: 'auto' }}>
      <div onClick={e => e.stopPropagation()}
        style={{ background: '#fff', borderRadius: '12px', padding: '20px',
                 maxWidth: '420px', width: '100%', maxHeight: '90vh', overflow: 'auto' }}>
        <h3 style={{ fontSize: '16px', fontWeight: '700', color: '#18181B', margin: '0 0 14px' }}>
          {isNew ? '직원 추가' : '직원 수정'}
        </h3>

        <Field label="이름 *">
          <input value={v.name} onChange={e => set('name', e.target.value)} style={inp} placeholder="예: 김철수" />
        </Field>

        <Field label="직책">
          <input value={v.position} onChange={e => set('position', e.target.value)} style={inp} placeholder="예: 주방장 / 홀 / 알바" />
        </Field>

        <Field label="고용 형태">
          <select value={v.type} onChange={e => set('type', e.target.value)} style={inp}>
            {EMP_TYPES.map(t => <option key={t.id} value={t.id}>{t.label} — {t.desc}</option>)}
          </select>
        </Field>

        {v.type === 'daily' ? (
          <div>
            <Field label="일급 (원)">
              <input type="number" value={v.dailyRate || ''} onChange={e => set('dailyRate', Number(e.target.value) || 0)}
                style={{ ...inp, textAlign: 'right' }} placeholder="0" />
            </Field>
            <div style={{
              fontSize: 11, color: '#78350f', background: '#FAFAFA',
              padding: '8px 10px', borderRadius: 6, border: '1px dashed #D4D4D8',
              lineHeight: 1.6,
            }}>
              💡 <strong>일용직 세무 규칙</strong> (소득세법 시행령 19조)<br/>
              · 일급 187,000원 이하 — 비과세, 원천징수 0원<br/>
              · 187,000원 초과 — 초과분 × 6.6% 원천징수 (소득세 6% + 지방세 0.6%)<br/>
              · 4대보험 가입 의무 X (산재만 사업주 부담)<br/>
              · 3개월 이상 연속 근로 시 일용직 자격 상실 → 정규직 전환 검토
            </div>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
            <Field label="시급 (원)">
              <input type="number" value={v.hourlyWage || ''} onChange={e => set('hourlyWage', Number(e.target.value) || 0)}
                style={{ ...inp, textAlign: 'right' }} placeholder="0" />
            </Field>
            <Field label="월급 (원, 정직원)">
              <input type="number" value={v.monthlyWage || ''} onChange={e => set('monthlyWage', Number(e.target.value) || 0)}
                style={{ ...inp, textAlign: 'right' }} placeholder="0" />
            </Field>
          </div>
        )}
        {v.type === 'shortterm' && (
          <div style={{
            fontSize: 11, color: '#78350f', background: '#FAFAFA',
            padding: '8px 10px', borderRadius: 6, border: '1px dashed #D4D4D8',
            lineHeight: 1.6, marginTop: 4,
          }}>
            💡 <strong>단기 근로자</strong> — 월 근무시간이 60시간 미만이면 4대보험 자동 면제 + 3.3% 원천징수만 적용됩니다. 60시간 이상이면 정규 알바와 동일.
          </div>
        )}

        <Field label="4대보험 가입">
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', cursor: 'pointer' }}>
            <input type="checkbox" checked={!!v.fourInsurance?.enabled}
              onChange={e => setIns('enabled', e.target.checked)} />
            <span>가입 (정직원·고정 알바)</span>
          </label>
          {v.fourInsurance?.enabled && (
            <div style={{ marginTop: '8px' }}>
              <span style={{ fontSize: '12px', color: '#6b7280', marginRight: '8px' }}>부양가족 수:</span>
              <input type="number" value={v.fourInsurance?.dependents || 1} min="1" max="10"
                onChange={e => setIns('dependents', Number(e.target.value) || 1)}
                style={{ ...inp, width: '80px', display: 'inline-block' }} />
            </div>
          )}
        </Field>

        <Field label="입사일">
          <input type="date" value={v.hireDate || ''} onChange={e => set('hireDate', e.target.value)} style={inp} />
        </Field>

        <Field label="">
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', cursor: 'pointer' }}>
            <input type="checkbox" checked={!!v.active} onChange={e => set('active', e.target.checked)} />
            <span>활성 (체크 해제 시 휴직 처리, 급여 계산 제외)</span>
          </label>
        </Field>

        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '16px' }}>
          <button onClick={onCancel}
            style={{ padding: '9px 18px', borderRadius: '8px', border: '1px solid #e5e7eb',
                     background: '#fff', color: '#374151', fontSize: '13px', cursor: 'pointer' }}>
            취소
          </button>
          <button onClick={onSave}
            style={{ padding: '9px 18px', borderRadius: '8px', border: 'none',
                     background: '#FF6F0F', color: '#fff', fontSize: '13px',
                     fontWeight: '700', cursor: 'pointer' }}>
            저장
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div style={{ marginBottom: '12px' }}>
      {label && (
        <div style={{ fontSize: '12px', fontWeight: '700', color: '#6b7280', marginBottom: '4px' }}>
          {label}
        </div>
      )}
      {children}
    </div>
  );
}
